import React, { useEffect, useMemo, useState } from 'react';
import LocalPharmacyDashboardPage from './LocalPharmacyDashboardPage';
import { PharmacyContext } from '../central-pharmacy/pharmacyContext';
import { canRoleAccessLocalPharmacy, resolveLocalPharmacyStockLocation, isLocalPharmacyDutyCabinet } from '../../constants/siteCareModes';
import { useAuth } from '../../context/AuthContext';
import SiteService from '../../services/siteService';
import { useClinicalLayoutContext } from '../../hooks/useClinicalLayoutContext';
import { authService } from '../../services/api';
import { LOCAL_PHARMACY_COPY } from './localPharmacyCopy';
import './LocalPharmacy.css';

function LocalPharmacyState({ variant = 'info', title, children, meta }) {
  return (
    <div className={`local-pharmacy lp-state lp-state--${variant}`}>
      <div className="lp-state__icon" aria-hidden>
        {variant === 'warn' ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        )}
      </div>
      <div>
        {title ? <p className="lp-state__title">{title}</p> : null}
        <div className="lp-state__text">{children}</div>
        {meta ? <div className="lp-state__meta">{meta}</div> : null}
      </div>
    </div>
  );
}

export default function LocalPharmacyPage() {
  const { user } = useAuth();
  const outlet = useClinicalLayoutContext();
  const [reloadNonce, setReloadNonce] = useState(0);
  const [resolvedSite, setResolvedSite] = useState(null);
  const [siteLoading, setSiteLoading] = useState(false);
  const [siteLoadError, setSiteLoadError] = useState('');
  const [profileSites, setProfileSites] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const workSites =
    Array.isArray(outlet.workSites) && outlet.workSites.length > 0
      ? outlet.workSites
      : profileSites || [];
  const workSiteId = outlet.workSiteId || '';

  useEffect(() => {
    if (Array.isArray(outlet.workSites) && outlet.workSites.length > 0) {
      setProfileSites(null);
      return;
    }
    let cancelled = false;
    setProfileLoading(true);
    authService
      .getProfile()
      .then(({ sites }) => {
        if (!cancelled) setProfileSites(Array.isArray(sites) ? sites : []);
      })
      .catch(() => {
        if (!cancelled) setProfileSites([]);
      })
      .finally(() => {
        if (!cancelled) setProfileLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [outlet.workSites]);

  useEffect(() => {
    if (workSiteId) return;
    if (!workSites.length) return;
    if (typeof outlet.setWorkSiteId !== 'function') return;
    outlet.setWorkSiteId(String(workSites[0].id));
  }, [workSiteId, workSites, outlet.setWorkSiteId]);

  useEffect(() => {
    const siteId = workSiteId;
    if (!siteId) {
      setResolvedSite(null);
      setSiteLoadError('');
      return;
    }

    let cancelled = false;
    setSiteLoading(true);
    setSiteLoadError('');
    SiteService.getSiteById(siteId)
      .then((res) => {
        if (cancelled) return;
        if (res?.success && res?.data) {
          setResolvedSite(res.data);
          return;
        }
        setResolvedSite(null);
      })
      .catch((error) => {
        if (cancelled) return;
        setResolvedSite(null);
        setSiteLoadError(error.message || 'Chargement du site impossible.');
      })
      .finally(() => {
        if (!cancelled) setSiteLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [workSiteId]);

  const contextSite = workSites.find((s) => String(s.id) === String(workSiteId)) || null;
  const currentSite = resolvedSite || contextSite;
  const canAccess = canRoleAccessLocalPharmacy(user?.role, currentSite);
  const stockLocation = resolveLocalPharmacyStockLocation(user?.role, currentSite);
  const isDutyCabinet = isLocalPharmacyDutyCabinet(user?.role, currentSite);

  const contextValue = useMemo(
    () => ({
      workSites,
      workSiteId,
      setWorkSiteId: outlet.setWorkSiteId || (() => {}),
      centralSite: null,
      toast: outlet.toast || (() => {}),
      reloadNonce,
      bumpReload: () => setReloadNonce((n) => n + 1),
      stockLocation,
      isDutyCabinet,
      mode: 'site'
    }),
    [workSites, workSiteId, outlet.setWorkSiteId, outlet.toast, reloadNonce, stockLocation, isDutyCabinet]
  );

  if (profileLoading || siteLoading) {
    return (
      <LocalPharmacyState title={LOCAL_PHARMACY_COPY.LOADING}>
        {siteLoading ? LOCAL_PHARMACY_COPY.LOADING_SITE : LOCAL_PHARMACY_COPY.LOADING}
      </LocalPharmacyState>
    );
  }

  if (!workSiteId) {
    if (workSites.length > 0) {
      return (
        <LocalPharmacyState title={LOCAL_PHARMACY_COPY.INIT_SITE}>
          Sélection automatique du premier site affecté à votre profil.
        </LocalPharmacyState>
      );
    }
    return (
      <LocalPharmacyState variant="warn" title="Site requis">
        {LOCAL_PHARMACY_COPY.NO_SITES}
      </LocalPharmacyState>
    );
  }

  if (!canAccess) {
    const role = user?.role || 'inconnu';
    const siteName = currentSite?.name || 'site inconnu';
    const mode = currentSite?.site_care_mode || 'mode inconnu';
    return (
      <LocalPharmacyState variant="warn" title={LOCAL_PHARMACY_COPY.ACCESS_DENIED}>
        {siteLoadError ? (
          <>Configuration site indisponible ({siteLoadError}).</>
        ) : (
          <>
            Cette page n&apos;est pas disponible pour votre rôle sur le site sélectionné.
            <div className="lp-state__meta">
              Rôle : <strong>{role}</strong> · Site : <strong>{siteName}</strong> · Mode :{' '}
              <strong>{mode}</strong>
            </div>
          </>
        )}
      </LocalPharmacyState>
    );
  }

  return (
    <PharmacyContext.Provider value={contextValue}>
      <LocalPharmacyDashboardPage />
    </PharmacyContext.Provider>
  );
}
